// FreshnessStrip — a compact, board-honest "data as of" row for the levy
// dashboard. One chip per Mobius data source showing the best-available "as of"
// date + age, so the board can see that e.g. funds-available is current while
// open-encumbrances may lag a few days.
//
// Staleness is visually honest: green within threshold, amber when stale, red
// when the last pull failed, and a muted "—" when a source has never reported.
// Data: GET /api/finance/freshness (useFinanceFreshness).

import { Clock } from "lucide-react";
import {
  useFinanceFreshness,
  type FreshnessStatus,
  type SourceFreshness,
} from "../../hooks/useFinance";

// Render an "as of" value in Dale City's timezone (America/New_York, DST-aware),
// no seconds. Date-only strings (e.g. a snapshot's "2026-06-11") are shown as a
// calendar date with NO timezone shift; full timestamps (UTC, e.g.
// "2026-06-10T20:12:49Z") are converted to Eastern and rendered "Jun 10, 4:12 PM ET".
function fmtET(asOf: string | null): string {
  if (!asOf) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
    const [y, m, d] = asOf.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(asOf);
  const iso =
    (asOf.includes("T") ? asOf : asOf.replace(" ", "T")) + (hasZone ? "" : "Z");
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return asOf;
  return (
    dt.toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }) + " ET"
  );
}

const STATUS_CLS: Record<FreshnessStatus, string> = {
  ok: "bg-status-green/12 text-status-green border-status-green/30",
  stale: "bg-status-amber/15 text-status-amber border-status-amber/40",
  failed: "bg-status-red/15 text-status-red border-status-red/40",
  unknown: "bg-theme-overlay text-theme-muted border-theme-subtle",
};

function ageLabel(s: SourceFreshness): string {
  if (s.status === "failed") return "pull failed";
  if (s.age_days == null) return "no data";
  if (s.age_days === 0) return "today";
  return `${s.age_days}d ago`;
}

function FreshnessChip({ s }: { s: SourceFreshness }) {
  const cls = STATUS_CLS[s.status];
  const title =
    `${s.label}: ${s.as_of ? `as of ${fmtET(s.as_of)}` : "never pulled"}` +
    (s.origin === "derived"
      ? " (derived from the data, not a recorded pull time)"
      : "") +
    (s.note ? ` — ${s.note}` : "");
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`}
      title={title}
    >
      <span className="font-semibold">{s.label}</span>
      <span className="opacity-80">
        {s.as_of ? `as of ${fmtET(s.as_of)} · ${ageLabel(s)}` : ageLabel(s)}
        {s.origin === "derived" && s.as_of ? " ~" : ""}
      </span>
    </span>
  );
}

/**
 * @param sources  which sources to show (default: snapshot + bc_balance +
 *                 open_po — the three the board cares about). full_xml is
 *                 plumbing and hidden by default.
 */
export default function FreshnessStrip({
  sources = ["snapshot", "bc_balance", "open_po"],
  className = "",
}: {
  sources?: SourceFreshness["source"][];
  className?: string;
}) {
  const { data, isLoading } = useFinanceFreshness();
  if (isLoading && !data) return null;
  const rows = (data?.sources ?? []).filter((s) => sources.includes(s.source));
  if (rows.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="inline-flex items-center gap-1 text-xs text-theme-muted">
        <Clock size={13} aria-hidden="true" />
        Data freshness:
      </span>
      {rows.map((s) => (
        <FreshnessChip key={s.source} s={s} />
      ))}
    </div>
  );
}
